import type {
  BulkInvitationJobDto,
  CoachDetailDto,
  CoachDto,
  CoachListDto,
  CreateCoachRequest,
  CreateStaffRequest,
  StaffDto,
  StaffListDto,
  UpdateCoachRequest,
  UpdateStaffRequest,
  UpdateEmailResult,
  DashboardDto,
  ParticipantDetailDto,
  ParticipantListDto,
  CycleListDto,
  CycleStateDto,
  Locale,
  RaterDetailDto,
  RaterListDto,
  RaterRelationship,
} from '@genoma/shared';

import { del, get, patch, post, put } from './api.ts';

/** Without a cycle the API answers about the one currently collecting answers. */
export const fetchDashboard = (cycleId?: string) =>
  get<DashboardDto>(`/dashboard${cycleId ? `?cycleId=${cycleId}` : ''}`);

export const fetchParticipants = (filters?: { search?: string; area?: string }) => {
  const params = new URLSearchParams();
  if (filters?.search) params.set('search', filters.search);
  if (filters?.area) params.set('area', filters.area);
  const query = params.toString();
  return get<ParticipantListDto>(`/dashboard/participants${query ? `?${query}` : ''}`);
};

export const fetchParticipant = (participantId: string) =>
  get<ParticipantDetailDto>(`/dashboard/participants/${participantId}`);


/** Correct where this person's invitations go. Keyed on the person. */
export const updatePersonEmail = (personId: string, email: string) =>
  patch<UpdateEmailResult>(`/dashboard/people/${personId}/email`, { email });

/** Send one person the link to one of their questionnaires. */
export const sendInvitation = (personId: string, surveyId: string) =>
  post<void>(`/dashboard/people/${personId}/invitation`, { surveyId });

/**
 * Give a leader the account they read their own report with.
 *
 * Not the survey invitation beside it: that sends a magic link to a
 * questionnaire. This sends credentials, and grants the role that lets them
 * open their report at all.
 */
export const sendParticipantAccess = (personId: string) =>
  post<StaffDto>(`/dashboard/people/${personId}/access`, {});

/** Set the language somebody's questionnaire renders in. */
export const updatePersonLocale = (personId: string, locale: Locale | null) =>
  patch<{ locale: Locale | null }>(`/dashboard/people/${personId}/locale`, { locale });

/** Everyone asked to assess somebody in this cycle. */
export const fetchRaters = (search?: string) =>
  get<RaterListDto>(`/dashboard/raters${search ? `?search=${encodeURIComponent(search)}` : ''}`);

export const fetchRater = (personId: string) =>
  get<RaterDetailDto>(`/dashboard/raters/${personId}`);

/**
 * Add somebody to the cycle as an assessor, with nobody on their list yet.
 *
 * Admin only. Assigning people and inviting them are separate acts afterwards,
 * and the API refuses to invite a questionnaire that covers nobody.
 */
export const createRater = (input: {
  fullName: string;
  email: string;
  jobTitle: string | null;
  area: string | null;
}) => post<RaterListDto>('/dashboard/raters', input);

/**
 * Take one person off a rater's list. Admin only; the API enforces it, and it
 * refuses once anything has been answered about that person.
 *
 * Returns the rater's refreshed detail, so the screen redraws from the server's
 * account of the list rather than from its own guess at it.
 */
export const removeRaterAssignment = (personId: string, participantId: string) =>
  del<RaterDetailDto>(`/dashboard/raters/${personId}/assignments/${participantId}`);

/**
 * Put one person on a rater's list. Admin only, and only before that rater has
 * opened their questionnaire — the API refuses afterwards.
 *
 * The relationship travels with the request because it decides which segment
 * the answers land in; there is no sensible default for it.
 */
export const addRaterAssignment = (
  personId: string,
  participantId: string,
  relationship: Exclude<RaterRelationship, 'SELF'>,
) =>
  post<RaterDetailDto>(`/dashboard/raters/${personId}/assignments`, {
    participantId,
    relationship,
  });


/**
 * The paced run through everybody who has never been invited.
 *
 * Reading it is open to anyone who can see the rater list; starting and
 * stopping it are admin-only and the API refuses anyone else.
 */
export const fetchInvitationRun = () =>
  get<BulkInvitationJobDto>('/dashboard/invitations/run');

export const startInvitationRun = () =>
  post<BulkInvitationJobDto>('/dashboard/invitations/run', {});

export const cancelInvitationRun = () =>
  del<BulkInvitationJobDto>('/dashboard/invitations/run');


/** Every cycle of the organization, for the selector on the rail. */
export const fetchCycles = () => get<CycleListDto>('/dashboard/cycles');

/** Whether the surveys are accepting answers. */
export const fetchCycleState = () => get<CycleStateDto>('/dashboard/cycle');

/** Open or close them. Admin only; the API refuses anyone else. */
export const setCycleStatus = (status: 'OPEN' | 'CLOSED') =>
  patch<CycleStateDto>('/dashboard/cycle', { status });


// ------------------------------------------------------------------ coaches

/** Everyone who coaches on this programme. Admin only; the API enforces it. */
export const fetchCoaches = () => get<CoachListDto>('/coaches');

export const fetchCoach = (personId: string) =>
  get<CoachDetailDto>(`/coaches/${personId}`);

export const createCoach = (input: CreateCoachRequest) =>
  post<CoachDto>('/coaches', input);

export const updateCoach = (personId: string, changes: UpdateCoachRequest) =>
  patch<CoachDto>(`/coaches/${personId}`, changes);

/**
 * Send a coach their way in. Every send mints a new password, so this also
 * resets one for somebody who had already chosen their own.
 */
export const inviteCoach = (personId: string) =>
  post<CoachDto>(`/coaches/${personId}/invitation`, {});

/** The whole list, not a delta: what the screen shows is what is saved. */
export const setCoachees = (personId: string, participantIds: string[]) =>
  put<CoachDetailDto>(`/coaches/${personId}/coachees`, { participantIds });


// ---------------------------------------------------------------- P&O staff

/** The client's own P&O accounts. Admin only; the API enforces it. */
export const fetchStaff = () => get<StaffListDto>('/staff');

export const createStaff = (input: CreateStaffRequest) => post<StaffDto>('/staff', input);

/**
 * Correct their name, address or phone.
 *
 * The address is the interesting one: the API moves the account's sign-in
 * address with it, so this is both "where the invitation goes" and "what they
 * type to get in". They are the same field on purpose — the alternative is an
 * account whose two addresses disagree and nobody notices until somebody
 * cannot sign in.
 */
export const updateStaff = (personId: string, changes: UpdateStaffRequest) =>
  patch<StaffDto>(`/staff/${personId}`, changes);

export const inviteStaff = (personId: string) =>
  post<StaffDto>(`/staff/${personId}/invitation`, {});
